import ModalWindows from "@/components/ModalWindows";
import { SettingsMenu } from "@/components/layout/settings/SettingsMenu";
import { DesktopNavItem } from "@/components/navbar/DesktopNavItem";
import { Button } from "@/components/ui/Button";
import { useAdminContext } from "@/context/AdminContext";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { useSessionContext } from "@/context/SessionContext";
import { useWatchDogContext } from "@/context/WatchDogContext";
import { storePendingAuthMode } from "@/lib/auth-navigation";
import {
  ArrowUpCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { LockClosedIcon, XMarkIcon } from "@heroicons/react/24/solid";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React from "react";
import { useEffect, useState } from "react";

type MobileAccordionItemProps = {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | null;
  isOpen: boolean;
  label: string;
  onToggle: () => void;
};

type MobileSubmenuItemProps = {
  item: {
    name: string;
    href: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | null;
  };
  onClick: () => void;
};

const MobileAccordionItem = ({
  icon: Icon,
  isOpen,
  label,
  onToggle,
}: MobileAccordionItemProps) => (
  <Button
    type="button"
    variant="unstyled"
    onClick={onToggle}
    className="group settings-row h-auto justify-start gap-x-3 p-1"
  >
    <div className="settings-icon-wrapper">
      {Icon ? (
        <Icon aria-hidden="true" className="settings-icon" />
      ) : (
        <Image
          aria-hidden="true"
          height={16}
          width={16}
          alt="paw"
          src="/image/paw/paw-dark.svg"
          className="settings-icon"
        />
      )}
    </div>

    <div className="min-w-0 flex-auto">
      <span className="settings-label truncate">{label}</span>
    </div>

    <ChevronDownIcon
      className={`settings-chevron ${isOpen ? "rotate-180" : ""}`}
    />
  </Button>
);

const MobileSubmenuItem = ({ item, onClick }: MobileSubmenuItemProps) => (
  <div className="relative pl-4">
    <DesktopNavItem item={item} onClick={onClick} />
  </div>
);

const MobileNav = () => {
  const {
    isLoggedIn,
    handleLogout,
    mobileMenuOpen,
    setMobileMenuOpen,
    PRODUCTS,
    NAVBAR_ITEMS,
    PROFILE_ITEMS,
    blog,
    userInfoGlobal,
    setDoLogin,
  } = useAppContext();

  const { LOGGED_IN_ITEMS, USER_ESTATE_ITEMS, setIsOpen, setSelectedItem } =
    useMenuItemsContext();
  const { isInitialLoading, setDoForgottenPassword } = useSessionContext();
  const { ADMIN_ITEMS, isAdmin } = useAdminContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      changeMembership: "Změnit členství",
      membership: "Členství",
      membershipOverview: "Správa nemovitostí",
      upgrade: "Zvýšit členství",
      administration: "Administrace",
      watchdog: "Hlídací pes",
      portfolio: "Mé portfolio",
      profile: "Profil",
      logout: "Odhlásit se",
      login: "Přihlášení",
      register: "Registrace",
    },
    en: {
      changeMembership: "Change membership",
      membership: "Membership",
      membershipOverview: "Property management",
      upgrade: "Upgrade now",
      administration: "Administration",
      watchdog: "WatchDog",
      portfolio: "Portfolio",
      profile: "Profile",
      logout: "Log out",
      login: "Log in",
      register: "Register",
    },
  }[language];

  const { isDisabled } = useWatchDogContext();
  const pathname = usePathname();
  const isNewPage = pathname === "/";
  const isPortfolioPage = pathname === "/portfolio";
  const isPublicProductPage = isPortfolioPage || pathname === "/hlidaci-pes";
  const isPublicMarketingPage = isNewPage || isPublicProductPage;
  const authHref = isNewPage ? "#prihlaseni" : "/#prihlaseni";
  const [isMounted, setIsMounted] = useState(false);
  const [isMembershipOpen, setIsMembershipOpen] = useState(false);
  const guestNavItems = NAVBAR_ITEMS.filter((item) => {
    if (item.href === "/blog") return false;
    if (item.href === "/#prihlaseni") return false;
    if (isNewPage) {
      if (item.href === "/#ukazka") return false;
      return [
        "/#produkty",
        "/portfolio",
        "/hlidaci-pes",
        "/#cenik",
        "/#faq",
      ].includes(item.href);
    }
    return true;
  }).map((item) => {
    if (!isNewPage) return item;
    if (item.href === "/#o-psovi") return { ...item, href: "#o-psovi" };
    if (item.href === "/#cenik") return { ...item, href: "#cenik" };
    if (item.href === "/#recenze") return { ...item, href: "#recenze" };
    return item;
  });
  const productItems = isNewPage
    ? PRODUCTS.map((item) => ({ ...item, href: "#cenik" }))
    : PRODUCTS;
  const isBasicClient = userInfoGlobal?.membership === "BASIC";
  const openUpgradeModal = () => {
    setSelectedItem({
      name: "Změnit členství",
      href: "",
      icon: ArrowUpCircleIcon,
    });
    setMobileMenuOpen(false);
    setIsOpen(true);
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <div
        className={`
    fixed inset-y-0 right-0 z-50 bg-background
    transform transition-transform duration-300 ease-in-out
    ${mobileMenuOpen ? "translate-x-0" : "translate-x-full"}
    w-full md:w-[400px]
    flex flex-col
  `}
      >
        {/* Close button */}
        <div className="flex justify-between items-center p-4 border-b">
          <Image
            src="/image/logos/SOFTIQ-hlidaci-pes.svg"
            alt="logo"
            width={120}
            height={30}
          />
          <Button
            type="button"
            variant="unstyled"
            aria-label="Close menu"
            className="desktop-nav__mobile-toggle"
            onClick={() => setMobileMenuOpen(false)}
          >
            <XMarkIcon
              aria-hidden="true"
              className="desktop-nav__mobile-toggle-icon"
            />
          </Button>
        </div>

        {/* Menu items */}
        <nav className="flex flex-1 flex-col items-center justify-start gap-1 overflow-y-auto px-4 pb-4">
          {!isMounted ? null : (
            <>
              {!isLoggedIn && !isInitialLoading && (
                <>
                  {/* Menu items group */}
                  <div className="w-full pt-1 space-y-1">
                    <p className="text-xs font-semibold uppercase text-softiq-secondary  tracking-wide">
                      {copy.membershipOverview}
                    </p>

                    <div className="space-y-1">
                      <MobileAccordionItem
                        icon={PRODUCTS[0]?.icon ?? null}
                        isOpen={isMembershipOpen}
                        label={copy.membership}
                        onToggle={() => setIsMembershipOpen((prev) => !prev)}
                      />

                      {isMembershipOpen && (
                        <div className="space-y-1">
                          {productItems.map((item) => (
                            <MobileSubmenuItem
                              key={item.name}
                              item={item}
                              onClick={() => setMobileMenuOpen(false)}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {guestNavItems.map((item, idx) => (
                      <DesktopNavItem item={item} key={idx} />
                    ))}

                    {isPublicMarketingPage && (
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        <Button
                          asChild
                          variant="outline"
                          size="compact"
                          className="w-full"
                        >
                          <Link
                            href={authHref}
                            onClick={() => {
                              setDoLogin(true);
                              storePendingAuthMode("login");
                              setDoForgottenPassword(false);
                              setMobileMenuOpen(false);
                            }}
                          >
                            {copy.login}
                          </Link>
                        </Button>
                        <Button
                          asChild
                          variant="brand"
                          size="compact"
                          className="w-full"
                        >
                          <Link
                            href={authHref}
                            onClick={() => {
                              setDoLogin(false);
                              storePendingAuthMode("register");
                              setDoForgottenPassword(false);
                              setMobileMenuOpen(false);
                            }}
                          >
                            {copy.register}
                          </Link>
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {isLoggedIn && !isInitialLoading && (
                <>
                  {isBasicClient && (
                    <Button
                      type="button"
                      variant="brand"
                      size="compact"
                      className="w-full"
                      onClick={openUpgradeModal}
                      startIcon={
                        <ArrowUpCircleIcon className="size-5 flex-none" />
                      }
                    >
                      {copy.upgrade}
                    </Button>
                  )}

                  {/* Admin group */}
                  {isAdmin && (
                    <div className="w-full pt-1 space-y-1">
                      <p className="text-xs font-semibold uppercase text-softiq-secondary  tracking-wide">
                        {copy.administration}
                      </p>

                      {ADMIN_ITEMS.map((item, idx) => (
                        <DesktopNavItem item={item} key={idx} />
                      ))}
                    </div>
                  )}

                  {/* Hlídací pes group */}
                  <div className="w-full pt-1 space-y-1">
                    <p className="text-xs font-semibold uppercase text-softiq-secondary  tracking-wide">
                      {copy.watchdog}
                    </p>

                    {LOGGED_IN_ITEMS.map((item, idx) => {
                      const isFirstItemDisabled = idx === 0 && isDisabled;
                      if (isFirstItemDisabled) {
                        return;
                      }
                      return <DesktopNavItem item={item} key={idx} />;
                    })}
                  </div>
                  {/* Mé portfolio group */}
                  <div className="w-full pt-1 space-y-1">
                    <p className="text-xs font-semibold uppercase text-softiq-secondary  tracking-wide">
                      {copy.portfolio}
                    </p>

                    {USER_ESTATE_ITEMS.map((item, idx) => (
                      <DesktopNavItem item={item} key={idx} />
                    ))}
                  </div>
                  {/*Profil group */}
                  <div className="w-full pt-1 space-y-1">
                    <p className="text-xs font-semibold uppercase text-softiq-secondary  tracking-wide">
                      {copy.profile}
                    </p>

                    {PROFILE_ITEMS.map((item, idx) => (
                      <DesktopNavItem item={item} key={idx} />
                    ))}
                    <div className="w-full pt-1 space-y-1">
                      <DesktopNavItem
                        item={{
                          name: copy.logout,
                          href: "",
                          icon: LockClosedIcon,
                        }}
                        key={1}
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleLogout();
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {isMounted && !isInitialLoading ? (
            <div className="mt-auto w-full border-t border-border/70 pt-3">
              <SettingsMenu
                isMobile
                switchId={
                  isLoggedIn ? "mobile-theme-mode" : "mobile-guest-theme-mode"
                }
                onAfterAction={() => setMobileMenuOpen(false)}
              />
            </div>
          ) : null}
        </nav>
      </div>
      <ModalWindows />
    </>
  );
};

export default MobileNav;
